"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/hooks/useAuth";
import { authFetch } from "@/lib/authFetch";
import CdnImage from "@/components/CdnImage";
import Link from "next/link";

// ── Types ──

interface FFProfileData {
  type: "ff";
  id: string;
  username: string;
  name: string | null;
  image: string | null;
  cover_url: string | null;
  bio: string | null;
  followers_count: number;
  following_count: number;
  copiers_count: number;
  wallet: string | null;
}

interface TraderProfileData {
  type: "trader";
  address: string;
  username: string | null;
  pseudonym: string | null;
  avatar: string | null;
  bio: string | null;
  totalPnL: number;
  totalVolume: number;
  profitPercent: number;
  winRate: number;
  tradeCount: number;
  marketsCount: number;
  isFrenFlowUser: boolean;
  frenFlowUsername: string | null;
  frenFlowName: string | null;
  frenFlowAvatar: string | null;
}

type HoverCardData = FFProfileData | TraderProfileData;

// ── Caches ──

const dataCache = new Map<string, { data: HoverCardData; ts: number }>();
const followCache = new Map<string, { isFollowing: boolean; ts: number }>();
const CACHE_TTL = 60_000;

// ── Helpers ──

function formatCompact(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toFixed(0);
}

// ── Component ──

interface UserHoverCardProps {
  username?: string;       // FrenFlow username
  walletAddress?: string;  // Polymarket wallet (fallback for traders)
  children: React.ReactNode;
}

export default function UserHoverCard({ username, walletAddress, children }: UserHoverCardProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [data, setData] = useState<HoverCardData | null>(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; placement: "below" | "above" }>({ top: 0, left: 0, placement: "below" });

  const triggerRef = useRef<HTMLSpanElement>(null);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchedRef = useRef(false);

  const { isLoggedIn, currentUserId } = useAuth();

  const isTouchDevice = typeof window !== "undefined" && ("ontouchstart" in window || navigator.maxTouchPoints > 0);

  // Nothing to show → just render children
  if ((!username && !walletAddress) || isTouchDevice) {
    return <>{children}</>;
  }

  const cacheKey = username || walletAddress || "";

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const cardWidth = 320;
    const cardHeight = 260;
    const gap = 8;
    const spaceBelow = window.innerHeight - rect.bottom;
    const placement = spaceBelow < cardHeight + gap ? "above" : "below";

    let left = rect.left;
    left = Math.max(12, Math.min(left, window.innerWidth - cardWidth - 12));

    const top = placement === "below"
      ? rect.bottom + window.scrollY + gap
      : rect.top + window.scrollY - cardHeight - gap;

    setPosition({ top, left, placement });
  }, []);

  const fetchData = useCallback(async () => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    // Check cache
    const cached = dataCache.get(cacheKey);
    if (cached && Date.now() - cached.ts < CACHE_TTL) {
      setData(cached.data);
      if (cached.data.type === "ff") {
        const fCached = followCache.get(cached.data.id);
        if (fCached && Date.now() - fCached.ts < CACHE_TTL) {
          setIsFollowing(fCached.isFollowing);
        }
      }
      return;
    }

    // Strategy: try FF profile first (if username), then trader profile (if wallet)
    if (username) {
      try {
        const res = await fetch(`/api/users/${encodeURIComponent(username)}/profile`);
        if (res.ok) {
          const json = await res.json();
          if (json?.user) {
            const ffData: FFProfileData = { type: "ff", ...json.user };
            setData(ffData);
            dataCache.set(cacheKey, { data: ffData, ts: Date.now() });

            // Fetch follow status
            if (isLoggedIn && json.user.id) {
              const fCached = followCache.get(json.user.id);
              if (fCached && Date.now() - fCached.ts < CACHE_TTL) {
                setIsFollowing(fCached.isFollowing);
              } else {
                try {
                  const fRes = await authFetch(`/api/social/follow?targetUserId=${json.user.id}`);
                  if (fRes.ok) {
                    const fData = await fRes.json();
                    const following = !!fData?.isFollowing;
                    setIsFollowing(following);
                    followCache.set(json.user.id, { isFollowing: following, ts: Date.now() });
                  }
                } catch { /* ignore */ }
              }
            }
            return;
          }
        }
      } catch { /* fall through to trader */ }
    }

    // Fallback: fetch trader profile by wallet (or by username as identifier)
    const traderIdentifier = walletAddress || username;
    if (traderIdentifier) {
      try {
        const res = await fetch(`/api/traders/${encodeURIComponent(traderIdentifier)}`);
        if (res.ok) {
          const json = await res.json();
          if (json?.trader) {
            const t = json.trader;
            const traderData: TraderProfileData = {
              type: "trader",
              address: t.address,
              username: t.username,
              pseudonym: t.pseudonym,
              avatar: t.avatarOptimized || t.avatar,
              bio: t.bio,
              totalPnL: t.totalPnL || 0,
              totalVolume: t.totalVolume || 0,
              profitPercent: t.profitPercent || 0,
              winRate: t.winRate || 0,
              tradeCount: t.tradeCount || 0,
              marketsCount: t.marketsCount || 0,
              isFrenFlowUser: !!t.isFrenFlowUser,
              frenFlowUsername: t.frenFlowUsername || null,
              frenFlowName: t.frenFlowName || null,
              frenFlowAvatar: t.frenFlowAvatar || null,
            };
            setData(traderData);
            dataCache.set(cacheKey, { data: traderData, ts: Date.now() });
            // Also cache by wallet if we used username
            if (walletAddress && walletAddress !== cacheKey) {
              dataCache.set(walletAddress, { data: traderData, ts: Date.now() });
            }
            return;
          }
        }
      } catch { /* ignore */ }
    }
  }, [cacheKey, username, walletAddress, isLoggedIn]);

  const handleShow = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    showTimerRef.current = setTimeout(() => {
      calculatePosition();
      setIsVisible(true);
      fetchData();
    }, 400);
  }, [calculatePosition, fetchData]);

  const handleHide = useCallback(() => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    hideTimerRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 300);
  }, []);

  const handleCardEnter = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const handleCardLeave = useCallback(() => {
    hideTimerRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 300);
  }, []);

  useEffect(() => {
    return () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  const handleFollow = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (data?.type !== "ff" || !data.id || followLoading) return;

    setFollowLoading(true);
    const prevState = isFollowing;
    setIsFollowing(!prevState);

    try {
      const res = await authFetch("/api/social/follow", {
        method: "POST",
        body: JSON.stringify({ targetUserId: data.id }),
      });
      if (res.ok) {
        const json = await res.json();
        const newState = !!json?.isFollowing;
        setIsFollowing(newState);
        followCache.set(data.id, { isFollowing: newState, ts: Date.now() });
        setData(prev => prev?.type === "ff" ? {
          ...prev,
          followers_count: prev.followers_count + (newState ? 1 : -1)
        } : prev);
      } else {
        setIsFollowing(prevState);
      }
    } catch {
      setIsFollowing(prevState);
    } finally {
      setFollowLoading(false);
    }
  };

  // Derive display values from data
  const isOwnProfile = data?.type === "ff" && currentUserId && data.id === currentUserId;

  const getProfileUrl = (): string => {
    if (data?.type === "ff") return `/profile/${data.username}`;
    if (data?.type === "trader") {
      if (data.frenFlowUsername) return `/profile/${data.frenFlowUsername}`;
      return `/traders/@${data.address}`;
    }
    return username ? `/profile/${username}` : `/traders/@${walletAddress}`;
  };

  const getDisplayName = (): string => {
    if (data?.type === "ff") return data.name || data.username || username || "";
    if (data?.type === "trader") return data.frenFlowName || data.pseudonym || data.username || `${data.address.slice(0, 6)}...${data.address.slice(-4)}`;
    return username || (walletAddress ? `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}` : "");
  };

  // `username` from polymarket can be the raw wallet (0x…) — truncate
  // before prefixing with @ so the chip doesn't blow out the layout.
  const fmt = (u: string) =>
    u.startsWith('0x') ? `${u.slice(0, 6)}...${u.slice(-4)}` : `@${u}`;
  const getHandle = (): string => {
    if (data?.type === "ff") return fmt(data.username);
    if (data?.type === "trader") {
      if (data.frenFlowUsername) return fmt(data.frenFlowUsername);
      if (data.username) return fmt(data.username);
      return `${data.address.slice(0, 6)}...${data.address.slice(-4)}`;
    }
    return username ? fmt(username) : "";
  };

  const getAvatar = (): string | null => {
    if (data?.type === "ff") return data.image;
    if (data?.type === "trader") return data.frenFlowAvatar || data.avatar;
    return null;
  };

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleShow}
        onMouseLeave={handleHide}
        className="inline"
      >
        {children}
      </span>
      {typeof window !== "undefined" && createPortal(
        <AnimatePresence>
          {isVisible && (
            <motion.div
              initial={{ opacity: 0, scale: 0.97, y: position.placement === "below" ? -4 : 4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97, y: position.placement === "below" ? -4 : 4 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              onMouseEnter={handleCardEnter}
              onMouseLeave={handleCardLeave}
              style={{ position: "absolute", top: position.top, left: position.left, zIndex: 9999 }}
              className="w-80 rounded-[5px] bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#25252B] shadow-2xl pointer-events-auto"
            >
              {/* Cover */}
              <div className="h-14 rounded-t-[5px] overflow-hidden">
                {data?.type === "ff" && data.cover_url ? (
                  <CdnImage src={data.cover_url} alt="" width={320} height={56} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-50 dark:from-white/[0.04] dark:to-white/[0.02]" />
                )}
              </div>

              <div className="px-4 pb-3.5 -mt-5">
                {/* Avatar + action */}
                <div className="flex items-end justify-between mb-2">
                  <Link href={getProfileUrl()} onClick={() => setIsVisible(false)} className="block">
                    <div className="w-12 h-12 rounded-[5px] overflow-hidden bg-gray-200 dark:bg-[#0f0f12] ring-[3px] ring-white dark:ring-[#18181D] flex-shrink-0">
                      {getAvatar() ? (
                        <CdnImage src={getAvatar()!} alt={getDisplayName()} width={48} height={48} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-base font-bold text-gray-500 dark:text-gray-400">{getDisplayName()[0]?.toUpperCase()}</span>
                        </div>
                      )}
                    </div>
                  </Link>
                  {/* Follow button — only for FF profiles */}
                  {data?.type === "ff" && isLoggedIn && !isOwnProfile && (
                    <button
                      onClick={handleFollow}
                      disabled={followLoading}
                      className={`px-3.5 py-1 rounded-[5px] text-[12px] font-bold transition-colors mb-0.5 ${
                        isFollowing
                          ? "bg-gray-100 dark:bg-[#0F0F12] text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-[#25252B] hover:border-red-400 hover:text-red-500 dark:hover:border-red-400 dark:hover:text-red-500"
                          : "bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-700 dark:hover:bg-gray-200"
                      } disabled:opacity-50`}
                    >
                      {isFollowing ? "Following" : "Follow"}
                    </button>
                  )}
                  {/* View profile button — for traders */}
                  {data?.type === "trader" && (
                    <Link
                      href={getProfileUrl()}
                      onClick={() => setIsVisible(false)}
                      className="px-3.5 py-1 rounded-[5px] text-[12px] font-bold bg-gray-100 dark:bg-[#0f0f12] text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-[#25252B] hover:bg-gray-200 dark:hover:bg-[#151518] transition-colors mb-0.5"
                    >
                      View Profile
                    </Link>
                  )}
                </div>

                {/* Name + handle */}
                <Link href={getProfileUrl()} onClick={() => setIsVisible(false)} className="block mb-1.5 group">
                  <p className="text-[13px] font-bold text-gray-900 dark:text-white leading-tight truncate group-hover:underline">
                    {getDisplayName()}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{getHandle()}</p>
                </Link>

                {/* Bio */}
                {data && ((data.type === "ff" && data.bio) || (data.type === "trader" && data.bio)) && (
                  <p className="text-[12px] text-gray-600 dark:text-gray-400 line-clamp-2 mb-2.5 leading-relaxed">
                    {data.bio}
                  </p>
                )}

                {/* FF stats */}
                {data?.type === "ff" && (
                  <div className="flex items-center gap-3 text-xs">
                    <span>
                      <span className="font-bold text-gray-900 dark:text-white">{data.following_count}</span>{" "}
                      <span className="text-gray-500">Following</span>
                    </span>
                    <span>
                      <span className="font-bold text-gray-900 dark:text-white">{data.followers_count}</span>{" "}
                      <span className="text-gray-500">Followers</span>
                    </span>
                    {data.copiers_count > 0 && (
                      <span>
                        <span className="font-bold text-gray-900 dark:text-white">{data.copiers_count}</span>{" "}
                        <span className="text-gray-500">Copiers</span>
                      </span>
                    )}
                  </div>
                )}

                {/* Trader stats */}
                {data?.type === "trader" && (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-3 text-xs">
                      <span>
                        <span className={`font-bold ${Number(data.totalPnL) >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                          {Number(data.totalPnL) >= 0 ? "+" : ""}${formatCompact(Number(data.totalPnL || 0))}
                        </span>{" "}
                        <span className="text-gray-500">PnL</span>
                      </span>
                      <span>
                        <span className={`font-bold ${Number(data.profitPercent) >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                          {Number(data.profitPercent) >= 0 ? "+" : ""}{Number(data.profitPercent || 0).toFixed(1)}%
                        </span>{" "}
                        <span className="text-gray-500">ROI</span>
                      </span>
                      <span>
                        <span className="font-bold text-gray-900 dark:text-white">{Number(data.winRate || 0).toFixed(0)}%</span>{" "}
                        <span className="text-gray-500">Win</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span>
                        <span className="font-bold text-gray-900 dark:text-white">${formatCompact(Number(data.totalVolume || 0))}</span>{" "}
                        <span className="text-gray-500">Volume</span>
                      </span>
                      <span>
                        <span className="font-bold text-gray-900 dark:text-white">{data.tradeCount}</span>{" "}
                        <span className="text-gray-500">Trades</span>
                      </span>
                      <span>
                        <span className="font-bold text-gray-900 dark:text-white">{data.marketsCount}</span>{" "}
                        <span className="text-gray-500">Markets</span>
                      </span>
                    </div>
                  </div>
                )}

                {/* Loading skeleton */}
                {!data && (
                  <div className="space-y-2 mt-1">
                    <div className="h-3 bg-gray-200 dark:bg-[#0f0f12] rounded w-24 animate-pulse" />
                    <div className="h-2.5 bg-gray-200 dark:bg-[#0f0f12] rounded w-16 animate-pulse" />
                    <div className="h-2.5 bg-gray-200 dark:bg-[#0f0f12] rounded w-40 animate-pulse mt-2" />
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
